import type {
  WebsiteSection,
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteThemeConfig,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import {
  SUPPORTED_LANGUAGES,
  type TranslateParams,
} from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  interpolate,
  resolveTranslationValue,
} from 'src/modules/shared/utils/i18n.util';
import {
  isSameWebsiteHexColor,
  isWebsiteHexColor,
} from './website-brand-color.util';
import { buildWebsiteLegalSectionContent } from './website-section-input.util';
import type {
  WebsiteStartingColorKey,
  WebsiteStartingColors,
} from '../types/website-draft.types';

/** Translates a key, filling `{placeholder}` values from the params. */
type WebsiteDefaultsTranslate = (
  key: TranslationKey,
  params?: TranslateParams
) => string;

type WebsiteDraftConfig = WebsiteUpsertRequest['draftConfig'];
type WebsiteDraftContent = WebsiteDraftConfig['content'];

const STARTING_COLOR_KEYS: readonly WebsiteStartingColorKey[] = [
  'primaryColor',
  'secondaryColor',
  'accentColor',
];

/**
 * Colors every new draft got before designs supplied their own. A draft that
 * still carries one never had that color picked, so a design switch may
 * replace it.
 */
export const LEGACY_WEBSITE_STARTING_COLORS: Readonly<WebsiteStartingColors> = {
  primaryColor: '#0f172a',
  secondaryColor: '#1e293b',
  accentColor: '#0ea5e9',
};

/** Service lines a new draft starts with, in display order. */
const DEFAULT_SERVICE_KEYS: readonly TranslationKey[] = [
  'websites.settings.defaultContent.services.appraisal',
  'websites.settings.defaultContent.services.listing',
  'websites.settings.defaultContent.services.visits',
  'websites.settings.defaultContent.services.closing',
];

/**
 * Resolves the colors a draft starts with on a design: the design's own
 * default theme, with the legacy color for any it leaves out.
 *
 * @param template - Design the draft starts on, when one is selectable.
 * @returns A starting value for each theme color.
 */
export const resolveWebsiteTemplateStartingColors = (
  template?: WebsiteTemplateRegistryTemplateSummary
): WebsiteStartingColors => {
  const defaultTheme = template?.preview?.defaultTheme;
  const pickColor = (key: WebsiteStartingColorKey): string => {
    const value = defaultTheme?.[key];
    return value && isWebsiteHexColor(value)
      ? value
      : LEGACY_WEBSITE_STARTING_COLORS[key];
  };

  return {
    primaryColor: pickColor('primaryColor'),
    secondaryColor: pickColor('secondaryColor'),
    accentColor: pickColor('accentColor'),
  };
};

/**
 * Moves a draft's untouched colors to a newly selected design's own colors.
 * A color counts as untouched while it is empty, equals the previous design's
 * starting color, or equals the legacy one. Colors the agency picked keep
 * their value, including any set in this editing session.
 *
 * @param theme - Draft theme before the switch.
 * @param previousTemplate - Design selected before the switch, when known.
 * @param nextTemplate - Newly selected design.
 * @param pickedColorKeys - Colors the agency set in this editing session.
 * @returns The theme with untouched colors on the new design's defaults.
 */
export const applyWebsiteTemplateStartingColors = (
  theme: WebsiteThemeConfig | undefined,
  previousTemplate: WebsiteTemplateRegistryTemplateSummary | undefined,
  nextTemplate: WebsiteTemplateRegistryTemplateSummary,
  pickedColorKeys: ReadonlySet<WebsiteStartingColorKey> = new Set()
): WebsiteThemeConfig => {
  const previousColors = previousTemplate
    ? resolveWebsiteTemplateStartingColors(previousTemplate)
    : undefined;
  const nextColors = resolveWebsiteTemplateStartingColors(nextTemplate);
  const nextTheme: WebsiteThemeConfig = { ...theme };

  for (const key of STARTING_COLOR_KEYS) {
    const currentColor = theme?.[key];
    const untouched =
      !pickedColorKeys.has(key) &&
      (!currentColor?.trim() ||
        isSameWebsiteHexColor(
          currentColor,
          LEGACY_WEBSITE_STARTING_COLORS[key]
        ) ||
        isSameWebsiteHexColor(currentColor, previousColors?.[key]));

    if (untouched) {
      nextTheme[key] = nextColors[key];
    }
  }

  return nextTheme;
};

/**
 * Builds the home page headline Comos suggests for a site name.
 *
 * @param t - Translator of the editor's language.
 * @param siteName - Name shown on the site.
 * @returns The suggested headline.
 */
export const buildWebsiteDefaultHeadline = (
  t: WebsiteDefaultsTranslate,
  siteName: string
): string => t('websites.settings.defaultContent.headline', { siteName });

/**
 * Builds the editable texts a new draft starts with: the headline, the short
 * description, the services, and the legal page's notice and privacy text.
 * Comos owns these defaults; the agency can rewrite or clear each one, and a
 * cleared one is left out of the site.
 *
 * @param t - Translator of the editor's language.
 * @param siteName - Name shown on the site.
 * @returns Content fields and sections for the new draft.
 */
export const buildWebsiteDefaultContent = (
  t: WebsiteDefaultsTranslate,
  siteName: string
): Pick<
  WebsiteDraftContent,
  'headline' | 'description' | 'services' | 'sections'
> => {
  const services = DEFAULT_SERVICE_KEYS.map((key) => t(key));
  const sections: WebsiteSection[] = [
    {
      id: 'services',
      type: 'services',
      content: { inputText: services.join('\n'), items: services },
    },
    {
      id: 'legal',
      type: 'legal',
      content: buildWebsiteLegalSectionContent({
        text: t('websites.settings.defaultContent.legalText'),
        privacyText: t('websites.settings.defaultContent.privacyText'),
      }),
    },
  ];

  return {
    headline: buildWebsiteDefaultHeadline(t, siteName),
    description: t('websites.settings.defaultContent.description'),
    services,
    sections,
  };
};

const showsText = (value: string | undefined, expected: string): boolean =>
  value !== undefined && value.trim() === expected.trim();

/** Builds a suggested headline for a site name in one language. */
type WebsiteHeadlineBuilder = (siteName: string) => string;

/**
 * Builds the suggested headline in the editor's language and in every
 * supported language, so a headline suggested in another language than the
 * editor's is still recognized as untouched.
 */
const buildHeadlineBuilders = (
  t: WebsiteDefaultsTranslate
): WebsiteHeadlineBuilder[] => [
  (siteName) => buildWebsiteDefaultHeadline(t, siteName),
  ...SUPPORTED_LANGUAGES.map(
    (language): WebsiteHeadlineBuilder =>
      (siteName) =>
        interpolate(
          resolveTranslationValue(
            language,
            'websites.settings.defaultContent.headline'
          ),
          { siteName }
        )
  ),
];

/**
 * Renames the site in a draft and carries along the texts that still show
 * the previous name the way Comos wrote them: the browser tab title and the
 * suggested headline, which keeps the language it was suggested in. Texts
 * the agency rewrote keep their wording.
 *
 * @param draftConfig - Draft before the rename.
 * @param siteName - New name shown on the site.
 * @param t - Translator of the editor's language.
 * @returns The draft with the new name.
 */
export const renameWebsiteDraft = (
  draftConfig: WebsiteDraftConfig,
  siteName: string,
  t: WebsiteDefaultsTranslate
): WebsiteDraftConfig => {
  const previousName = draftConfig.content.siteName;
  const { seo } = draftConfig;
  const headlineBuilder = buildHeadlineBuilders(t).find((build) =>
    showsText(draftConfig.content.headline, build(previousName))
  );

  return {
    ...draftConfig,
    content: {
      ...draftConfig.content,
      siteName,
      ...(headlineBuilder ? { headline: headlineBuilder(siteName) } : {}),
    },
    ...(seo && showsText(seo.title, previousName)
      ? { seo: { ...seo, title: siteName } }
      : {}),
  };
};
